import { ResolvedCalendar, workingDaysBetween } from './calendarEngine';

/**
 * Fraction (0..1) of an activity's planned span [startDay, endDay] that has elapsed by
 * `today`. Mirrors backend/src/calendars/planned-fraction.ts exactly — this is the
 * single source of truth on the frontend, replacing what used to be two independent
 * copies in Budget.tsx and a third in Reporting.tsx, all doing the same calendar-time-
 * linear-interpolation formula. Calendar-aware (counts only working days) when
 * `calendar` is provided; otherwise falls back to the ORIGINAL calendar-day-linear
 * formula byte-for-byte, so behavior is unchanged when no project calendar is loaded
 * (feature flag off, or project has no calendar_id set).
 */
export function plannedElapsedFraction(
  projectStartDate: Date,
  startDay: number,
  endDay: number,
  today: Date,
  calendar?: ResolvedCalendar,
): number {
  if (calendar) {
    // setDate (not raw millisecond arithmetic) — safe across DST transitions, see
    // calendarEngine.ts's addCalDays for why `getTime() + n * 86400000` silently drifts
    // the computed calendar date by a day once the range crosses a DST change.
    const spanStart = new Date(projectStartDate);
    spanStart.setDate(spanStart.getDate() + startDay - 1);
    const spanEnd = new Date(projectStartDate);
    spanEnd.setDate(spanEnd.getDate() + Math.max(endDay, startDay) - 1);
    const totalWD = Math.max(1, workingDaysBetween(calendar, spanStart, spanEnd));
    if (today < spanStart) return 0;
    if (today > spanEnd) return 1;
    const elapsedWD = workingDaysBetween(calendar, spanStart, today);
    return Math.max(0, Math.min(1, elapsedWD / totalWD));
  }
  const actStart = new Date(projectStartDate.getTime());
  actStart.setDate(actStart.getDate() + startDay - 1);
  const actEnd = new Date(projectStartDate.getTime());
  actEnd.setDate(actEnd.getDate() + endDay);
  const totalMs = actEnd.getTime() - actStart.getTime();
  const elapsedMs = Math.max(0, Math.min(today.getTime() - actStart.getTime(), totalMs));
  return totalMs > 0 ? elapsedMs / totalMs : 0;
}
